import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { initializeAccessibility } from './utils/accessibility';
import { useEffect } from 'react';

// Pages
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ProfilePage } from './pages/ProfilePage';
import { CasesPage } from './pages/CasesPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { CreateCasePage } from './pages/CreateCasePage';
import { EditCasePage } from './pages/EditCasePage';
import { ArchivePage } from './pages/ArchivePage';
import { ForumPage } from './pages/ForumPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { EmailVerificationReminderPage } from './pages/EmailVerificationReminderPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

// Admin Pages
import { AdminPage } from './pages/AdminPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminCasesPage } from './pages/AdminCasesPage';
import { AdminForumPage } from './pages/AdminForumPage';
import { AdminAuditPage } from './pages/AdminAuditPage';

// Forum Components
import { ForumHomeEnhanced } from './components/ForumHomeEnhanced';
import { ForumCategory } from './components/ForumCategory';
import { ForumPost } from './components/ForumPost';
import { CreateForumPost } from './components/CreateForumPost';

import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  useEffect(() => {
    // Initialize accessibility features when app loads
    initializeAccessibility();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="App min-h-screen bg-primary-50 dark:bg-gray-900 transition-colors duration-300">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify" element={<EmailVerificationPage />} />
              <Route path="/email-verification-reminder" element={<EmailVerificationReminderPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Admin routes - Must come before main layout routes to avoid conflicts */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route index element={<AdminPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="cases" element={<AdminCasesPage />} />
                <Route path="forum" element={<AdminForumPage />} />
                <Route path="audit" element={<AdminAuditPage />} />
              </Route>
              
              {/* Protected routes with layout */}
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="cases" element={<CasesPage />} />
                <Route path="cases/:caseId" element={<CaseDetailPage />} />
                <Route path="cases/:caseId/edit" element={
                  <ProtectedRoute>
                    <EditCasePage />
                  </ProtectedRoute>
                } />
                <Route path="archive" element={<ArchivePage />} />
                
                {/* Forum routes */}
                <Route path="forum" element={<ForumHomeEnhanced />} />
                <Route path="forum/category/:categoryId" element={<ForumCategory />} />
                <Route path="forum/post/:postId" element={<ForumPost />} />
                
                {/* Protected routes that require authentication */}
                <Route path="profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="cases/new" element={
                  <ProtectedRoute>
                    <CreateCasePage />
                  </ProtectedRoute>
                } />
                <Route path="forum/create" element={
                  <ProtectedRoute>
                    <CreateForumPost />
                  </ProtectedRoute>
                } />
              </Route>
              
              {/* 404 route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
      
      {/* Development tools */}
      {(import.meta as any).env?.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;